import React, { memo, useState } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons'
import { Link } from 'react-router-dom'

import { isAdminDomain, isPartnerDomain, isSupportDomain } from '../../../../lib/currentDomain'
import { sortByString } from '../../../../lib/sort-by-string'
import { Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableBodyCell } from '../../../../lib/table'
import { WidgetLayout } from '../../../../lib/widget-layout'
import Text from '../../../../ui/Text'
import { Button } from '../../../../lib/button'
import ButtonGroupPagination from '../../../../ui/common/ButtonGroupPagination'
import { dateLocalize } from '../../../../lib/date-localize'
import { getProductName } from '../../../services/getProductName'
import { ChargersPagination } from '../../../../lib/chargers-pagination'
import { MeasurementDevicesGetModel, MeasurementDevicesGetFilteredApiResponse } from '../../../redux/api/web-api'
import { enhancedApi as webApi } from '../../../redux/api/web-api'
import useText from '../../../hooks/useText'

export const SIcon = styled(FontAwesomeIcon)`
    margin-left: 4px;
`
export const SWrapper = styled.div`
    @media (max-width: 900px) {
        padding-bottom: 15px;
    }
`

enum SortParameter {
    SERIALNUMBER = 'serialNumber',
    NAME = 'name',
    DESCRIPTION = 'description',
    STATUS = 'status',
    ORGANISATION = 'companyName',
    PRODUCT = 'productName',
    LASTSEEN = 'lastSeen',
}

export type MeasurementDevicesTableWidgetProps = {
    data: MeasurementDevicesGetFilteredApiResponse
    openAddModal?: () => void
    createDevice?: () => void
    removeDevice?: (device: MeasurementDevicesGetModel) => void
    loading?: boolean
    style?: React.CSSProperties
    currentPage: number
    handlePageClick: (selected: number) => void
    pageClick: () => void
}

export const MeasurementDevicesTableWidget = memo(function MeasurementDevicesTableWidget({
    style,
    data,
    openAddModal,
    loading,
    createDevice,
    removeDevice,
    handlePageClick,
    pageClick,
    currentPage,
}: MeasurementDevicesTableWidgetProps): React.ReactElement {
    const text = useText()
    const [sortFilter, setSortFilter] = useState<{ parameter: SortParameter; reversed: boolean }>({
        parameter: SortParameter.SERIALNUMBER,
        reversed: false,
    })

    const [pageSize, setPageSize] = useState(50)
    const offset = currentPage * pageSize

    const prefetchDeviceGraph = webApi.usePrefetch('loadBalancerGroupGetSnapshotByDeviceId')

    return (
        <WidgetLayout
            data-testid="md-table-list"
            style={style}
            title={text('lblMeasurementDevices')}
            headerButtons={[
                !isAdminDomain ? (
                    isSupportDomain ? (
                        <Button code="btnCreate" small onClick={createDevice} data-testid="create-button"></Button>
                    ) : (
                        <Button code="btnAdd" small onClick={openAddModal}></Button>
                    )
                ) : (
                    <></>
                ),
            ]}
        >
            {data?.length > 0 && (
                <span className="heading5" data-testid="info-no-md">
                    <Text code="lblMeasurementDevicesFound" values={{ measurementDevices: data?.length }}></Text>
                </span>
            )}
            <br />
            <div>
                {data?.length >= 100 && (
                    <>
                        <div className="text-right">
                            <div data-testid="pagination-title">
                                <Text code="msgResultPage"></Text>
                                <span>:</span>

                                <ButtonGroupPagination
                                    value={pageSize}
                                    onChange={(value: number) => {
                                        setPageSize(value), pageClick()
                                    }}
                                    data-testid="button-paginate-group"
                                ></ButtonGroupPagination>
                            </div>
                        </div>
                    </>
                )}
            </div>
            <br />

            <Table data-testid="table">
                <TableHeader>
                    <TableRow data-testid="table-row1">
                        <TableHeaderCell data-testid="table-th-serial">
                            <a
                                data-testid="table-th-serial-link"
                                onClick={() =>
                                    setSortFilter({
                                        parameter: SortParameter.SERIALNUMBER,
                                        reversed: !sortFilter?.reversed,
                                    })
                                }
                                className="pointer"
                            >
                                <Text code="lblSerialNumberShort"></Text>
                                {sortFilter?.parameter === SortParameter.SERIALNUMBER && (
                                    <SIcon data-testid="icon-sn" icon={sortFilter.reversed ? faCaretUp : faCaretDown} />
                                )}
                            </a>
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-stauts">
                            <a
                                data-testid="table-th-status-link"
                                onClick={() =>
                                    setSortFilter({
                                        parameter: SortParameter.STATUS,
                                        reversed: !sortFilter?.reversed,
                                    })
                                }
                                className="pointer"
                            >
                                <Text code="lblStatus"></Text>
                                {sortFilter?.parameter === SortParameter.STATUS && (
                                    <SIcon
                                        data-testid="icon-status"
                                        icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                    />
                                )}
                            </a>
                        </TableHeaderCell>

                        <TableHeaderCell data-testid="table-th-lastseen">
                            <a
                                data-testid="table-th-lastseen-link"
                                onClick={() =>
                                    setSortFilter({
                                        parameter: SortParameter.LASTSEEN,
                                        reversed: !sortFilter?.reversed,
                                    })
                                }
                                className="pointer"
                            >
                                <Text code="lblLastSeen"></Text>

                                {sortFilter?.parameter === SortParameter.LASTSEEN && (
                                    <SIcon
                                        data-testid="icon-seen"
                                        icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                    />
                                )}
                            </a>
                        </TableHeaderCell>

                        <TableHeaderCell data-testid="table-th-name">
                            <a
                                data-testid="table-th-name-link"
                                onClick={() =>
                                    setSortFilter({
                                        parameter: SortParameter.NAME,
                                        reversed: !sortFilter?.reversed,
                                    })
                                }
                                className="pointer"
                            >
                                <Text code="lblName"></Text>
                                {sortFilter?.parameter === SortParameter.NAME && (
                                    <SIcon
                                        data-testid="icon-name"
                                        icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                    />
                                )}
                            </a>
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-desc">
                            <a
                                data-testid="table-th-desc-link"
                                onClick={() =>
                                    setSortFilter({
                                        parameter: SortParameter.DESCRIPTION,
                                        reversed: !sortFilter?.reversed,
                                    })
                                }
                                className="pointer"
                            >
                                <Text code="lblDescription"></Text>
                                {sortFilter?.parameter === SortParameter.DESCRIPTION && (
                                    <SIcon
                                        data-testid="icon-desc"
                                        icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                    />
                                )}
                            </a>
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-organisation">
                            <a
                                data-testid="table-th-organisation-link"
                                onClick={() =>
                                    setSortFilter({
                                        parameter: SortParameter.ORGANISATION,
                                        reversed: !sortFilter?.reversed,
                                    })
                                }
                                className="pointer"
                            >
                                <Text code="lblOrganisation"></Text>
                                {sortFilter?.parameter === SortParameter.ORGANISATION && (
                                    <SIcon
                                        data-testid="icon-organisation"
                                        icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                    />
                                )}
                            </a>
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-product">
                            <a
                                data-testid="table-th-product-link"
                                onClick={() =>
                                    setSortFilter({
                                        parameter: SortParameter.PRODUCT,
                                        reversed: !sortFilter?.reversed,
                                    })
                                }
                                className="pointer"
                            >
                                <Text code="lblProduct"></Text>
                                {sortFilter?.parameter === SortParameter.PRODUCT && (
                                    <SIcon
                                        data-testid="icon-product"
                                        icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                    />
                                )}
                            </a>
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-active">
                            <Text code="lblActive"></Text>
                        </TableHeaderCell>
                        {isPartnerDomain && (
                            <TableHeaderCell data-testid="table-th-remove">
                                <Text code="lblRemove"></Text>
                            </TableHeaderCell>
                        )}
                    </TableRow>
                </TableHeader>

                <TableBody isLoading={loading} numberOfLoadingColumns={8}>
                    {data
                        ?.slice(offset, offset + pageSize)
                        .sort((a, b) =>
                            sortByString(
                                (a[sortFilter.parameter] as string) ?? '',
                                (b[sortFilter.parameter] as string) ?? '',
                                sortFilter.reversed
                            )
                        )
                        .map((device) => (
                            <TableRow key={device.id} data-testid="table-row-2">
                                <TableBodyCell data-testid={`table-tr-serial-${device.serialNumber}`}>
                                    <Link
                                        to={`/measurementDevice/${device.id}`}
                                        className="btn btn-link btn-xs"
                                        data-testid={`table-tr-serial-${device.serialNumber}-btn`}
                                        onMouseEnter={() => prefetchDeviceGraph({ measurementDeviceId: device.id })}
                                    >
                                        {device.serialNumber}
                                    </Link>
                                </TableBodyCell>

                                <TableBodyCell data-testid={`table-tr-status-${device.serialNumber}`}>
                                    {device.status == 'Online' ? (
                                        <span className="fa fa-wifi greenFont" title="Online"></span>
                                    ) : (
                                        <span className="fa fa-wifi redFont" title="Offline"></span>
                                    )}
                                </TableBodyCell>

                                <TableBodyCell data-testid={`table-tr-lastseen-${device.serialNumber}`}>
                                    {dateLocalize(device?.lastSeen)}
                                </TableBodyCell>

                                <TableBodyCell data-testid={`table-tr-name-${device.serialNumber}`}>
                                    {device.name || device.serialNumber}
                                </TableBodyCell>
                                <TableBodyCell data-testid={`table-tr-desc-${device.serialNumber}`}>
                                    {device.description}
                                </TableBodyCell>
                                <TableBodyCell data-testid={`table-tr-org-${device.serialNumber}`}>
                                    {device.companyName}
                                </TableBodyCell>
                                <TableBodyCell data-testid={`table-tr-product-${device.serialNumber}`}>
                                    {getProductName(device.productId)}
                                </TableBodyCell>
                                <TableBodyCell data-testid={`table-tr-active-${device.serialNumber}`}>
                                    {device.active ? (
                                        <Text code="lblActiveStatus"></Text>
                                    ) : (
                                        <Text code="lblInactiveStatus"></Text>
                                    )}
                                </TableBodyCell>

                                {isPartnerDomain && (
                                    <TableBodyCell data-testid={`table-tr-remove-${device.serialNumber}`}>
                                        <button
                                            type="button"
                                            data-testid="remove-button"
                                            data-toggle="tooltip"
                                            data-placement="top"
                                            onClick={() => removeDevice && removeDevice(device)}
                                            {...(device.loadBalancerGroupId !== undefined &&
                                                device.loadBalancerGroupId > 0 && { disabled: true })}
                                            title={
                                                device.loadBalancerGroupId !== 0
                                                    ? text('msgAmpGuardConnected')
                                                    : text('btnRemove')
                                            }
                                            className="btn btn-danger btn-xs btn-icon"
                                        >
                                            <span className="fa fa-remove" data-testid="remove"></span>
                                        </button>
                                    </TableBodyCell>
                                )}
                            </TableRow>
                        ))}
                </TableBody>
            </Table>

            {data?.length > 50 && (
                <ChargersPagination
                    setPageIndex={handlePageClick}
                    totalCount={data.length}
                    pageSize={pageSize}
                    pageIndex={currentPage}
                    displayedChargers={pageSize}
                />
            )}

            {loading && isAdminDomain && (
                <div className="shimmer-container shimmer-medium" data-testid="shimmer-container" />
            )}
        </WidgetLayout>
    )
})
